import get from "lodash/get";

const SESSION_STORAGE_PREFIX = "ai-chat-session";

function getSessionKey(productId, studentId) {
  return `${SESSION_STORAGE_PREFIX}-${productId}-${studentId}`;
}

let activeAbortController = null;

function getAuthHeaders() {
  const headers = { "Content-Type": "application/json" };
  const authHeader = window.axios.defaults.headers.common["Authorization"];
  if (authHeader) {
    headers["Authorization"] = authHeader;
  }
  return headers;
}

function ensureFreshToken() {
  const refreshToken = localStorage.getItem("_refreshToken");
  if (!refreshToken) return Promise.resolve();

  return window.axios
    .post("/accounts/refresh-token", { refresh: refreshToken })
    .then(() => {})
    .catch(() => {});
}

export default {
  // BFF proxy: the Members backend proxies to Velora's
  // /v1/agents/internal/by-product/{id}/ adding X-Internal-Token server-side.
  checkAgent(context) {
    const productId = context.rootGetters.activeProductId;
    if (!productId) return Promise.resolve();

    return new Promise((resolve, reject) => {
      window.axios
        .get(`/agents/check/${productId}`)
        .then(response => {
          context.commit("setAgentInfo", response.data);

          if (response.data.has_agent) {
            const studentId = get(context.rootState, "user.user.id", "");
            const savedSession = localStorage.getItem(
              getSessionKey(productId, studentId)
            );
            if (savedSession) {
              context.commit("setSessionId", savedSession);
            }
          }

          resolve(response.data);
        })
        .catch(error => {
          context.commit("setAgentInfo", { has_agent: false });
          reject(error);
        });
    });
  },

  // BFF proxy: the Members backend proxies to Velora's
  // /v1/agents/internal/by-product/{id}/chat adding X-Internal-Token server-side.
  // Uses native fetch() instead of axios because axios doesn't support SSE streaming.
  sendMessage(context, { message, _isRetry = false }) {
    const productId = context.rootGetters.activeProductId;
    const studentId = get(context.rootState, "user.user.id", "");

    if (!message.trim() || context.state.isStreaming) return Promise.resolve();

    context.commit("addMessage", {
      role: "user",
      content: message.trim()
    });

    context.commit("addMessage", {
      role: "assistant",
      content: "",
      isStreaming: true,
      sources: [],
      isError: false
    });

    context.commit("setStreaming", true);

    const body = {
      message: message.trim(),
      student_id: String(studentId)
    };
    if (context.state.sessionId) {
      body.session_id = context.state.sessionId;
    }

    if (activeAbortController) {
      activeAbortController.abort();
    }
    activeAbortController = new AbortController();

    const baseURL = window.axios.defaults.baseURL;

    return fetch(`${baseURL}/agents/chat/${productId}`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
      signal: activeAbortController.signal
    })
      .then(response => {
        if (response.status === 401 && !_isRetry) {
          context.state.messages.splice(-2, 2);
          context.commit("setStreaming", false);
          return ensureFreshToken().then(() =>
            context.dispatch("sendMessage", { message, _isRetry: true })
          );
        }

        if (!response.ok) {
          return response.json().then(err => {
            let errorMessage;
            if (response.status === 429) {
              errorMessage =
                "Aguarde alguns segundos antes de enviar nova mensagem.";
            } else if (response.status === 404) {
              errorMessage = "Agente indisponível no momento.";
            } else {
              errorMessage = err.detail || "Erro ao processar mensagem.";
            }
            context.commit("setLastMessageError", errorMessage);
            context.commit("setStreaming", false);
            activeAbortController = null;
          });
        }

        const newSessionId = response.headers.get("X-Session-ID");
        if (newSessionId) {
          context.commit("setSessionId", newSessionId);
          localStorage.setItem(
            getSessionKey(productId, studentId),
            newSessionId
          );
        }

        if (!response.body) {
          context.commit(
            "setLastMessageError",
            "Streaming não suportado neste navegador."
          );
          context.commit("setStreaming", false);
          activeAbortController = null;
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        function pump() {
          return reader.read().then(({ done, value }) => {
            if (done) {
              context.commit("setStreaming", false);
              activeAbortController = null;
              return;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";

            for (const line of lines) {
              if (!line.startsWith("data: ")) continue;

              try {
                const event = JSON.parse(line.slice(6));

                switch (event.type) {
                  case "chunk":
                    context.commit("appendToLastMessage", event.content);
                    break;

                  case "done":
                    context.commit("finalizeLastMessage", {
                      sources: event.sources || []
                    });
                    break;

                  case "error":
                    context.commit("setLastMessageError", event.content);
                    break;
                }
              } catch {
                // Incomplete SSE line, skip
              }
            }

            return pump();
          });
        }

        return pump();
      })
      .catch(error => {
        if (error.name === "AbortError") return;

        context.commit(
          "setLastMessageError",
          "Erro de conexão. Tente novamente."
        );
        context.commit("setStreaming", false);
        activeAbortController = null;
      });
  },

  toggleChat(context) {
    context.commit("setOpen", !context.state.isOpen);
  },

  openChat(context) {
    context.commit("setOpen", true);
  },

  closeChat(context) {
    context.commit("setOpen", false);
  },

  startNewConversation(context) {
    if (activeAbortController) {
      activeAbortController.abort();
      activeAbortController = null;
    }
    context.commit("setStreaming", false);

    const productId = context.rootGetters.activeProductId;
    const studentId = get(context.rootState, "user.user.id", "");

    context.commit("clearMessages");
    localStorage.removeItem(getSessionKey(productId, studentId));
  },

  cancelStream(context) {
    if (activeAbortController) {
      activeAbortController.abort();
      activeAbortController = null;
    }
    context.commit("setStreaming", false);
  }
};
